import React, {useState} from 'react'
import {Text, Stack, Box, Button, useMultiStyleConfig, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {AddIcon, CloseIcon, ChevronRight} from '../../icons'
import {remCalc} from '../../../theme/_utils'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import useDamImage from '../../../custom-hooks/use-dam-image'
import {Link} from 'react-router-dom'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const Card = ({
    title,
    subtitle,
    text,
    ctaLabel,
    ctaUrl,
    isSelected,
    onClick,
    noneSelected,
    reset,
    index,
    cardsLength,
    linkIsAbsoulte,
}) => {
    const styles = useMultiStyleConfig('ContentCards')
    const buttonProps = linkIsAbsoulte
        ? {
              as: 'a',
              href: ctaUrl,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: ctaUrl,
          }

    return (
        <Box
            flexGrow={isSelected || noneSelected ? 1 : 0}
            onClick={() => {
                if (!isSelected) {
                    onClick()
                }
            }}
            borderRight={index !== cardsLength - 1 ? {base: 'none', lg: 'large'} : 'none'}
            borderBottom={index !== cardsLength - 1 ? {base: 'large', lg: 'none'} : 'none'}
            display={isSelected || noneSelected ? 'flex' : 'flex'}
            alignItems={
                noneSelected
                    ? {base: 'center', lg: 'flex-end'}
                    : isSelected
                    ? {base: 'flex-start', lg: 'center'}
                    : {base: 'center', lg: 'flex-start'}
            }
            {...styles.cardContainer}
            background={!noneSelected ? 'rgba(0, 0, 0, 0.50)' : 'rgba(0, 0, 0, 0.30)'}
            minHeight={
                isSelected
                    ? {base: remCalc(411), lg: 'inherit'}
                    : noneSelected
                    ? {base: remCalc(144), lg: 'inherit'}
                    : {base: remCalc(55), lg: 'inherit'}
            }
            p={
                !noneSelected && !isSelected
                    ? {
                          base: `${remCalc(8)} ${remCalc(16)}`,
                          md: `${remCalc(8)} ${remCalc(24)}`,
                          lg: `${remCalc(24)}`,
                          xl: `${remCalc(24)}`,
                      }
                    : !noneSelected
                    ? {
                          base: ` ${remCalc(24)} ${remCalc(16)}`,
                          md: `${remCalc(52)} ${remCalc(24)}`,
                          lg: `${remCalc(24)}`,
                          xl: `${remCalc(40)} ${remCalc(24)} ${remCalc(40)} ${remCalc(260)}`,
                      }
                    : {
                          base: `0 ${remCalc(16)}`,
                          md: `0 ${remCalc(24)}`,
                          lg: `${remCalc(40)} ${remCalc(24)}`,
                          xl: `${remCalc(160)} ${remCalc(24)}`,
                      }
            }
        >
            {noneSelected && (
                <>
                    <HideOnMobile>
                        <Stack key={index} {...styles.card}>
                            <AddIcon {...styles.addIcon} />
                            <Text {...styles.subtitle}>{subtitle}</Text>
                            <Text {...styles.title}>{title}</Text>
                        </Stack>
                    </HideOnMobile>
                    <HideOnDesktop {...styles.fullContainer}>
                        <Flex key={index} {...styles.mobileCard}>
                            <Stack>
                                <Text {...styles.subtitle}>{subtitle}</Text>
                                <Text {...styles.title}>{title}</Text>
                            </Stack>
                            <AddIcon {...styles.addIcon} />
                        </Flex>
                    </HideOnDesktop>
                </>
            )}
            {!noneSelected && (
                <>
                    <HideOnMobile {...styles.fullContainer}>
                        <Text {...styles.selectedContainer}>
                            {isSelected ? (
                                <Stack
                                    {...styles.card}
                                    {...styles.selectedCard}
                                    maxWidth={remCalc(569)}
                                >
                                    <Text {...styles.subtitle}>{subtitle}</Text>
                                    <Text {...styles.title}>{title}</Text>
                                    <Text {...styles.text}>{text}</Text>
                                    {ctaLabel && ctaUrl && (
                                        <Button
                                            variant="inverse-icon-button"
                                            rightIcon={<ChevronRight {...styles.chevronRight} />}
                                            {...buttonProps}
                                        >
                                            {ctaLabel}
                                        </Button>
                                    )}
                                </Stack>
                            ) : (
                                <Text {...styles.title} style={{writingMode: 'vertical-rl'}}>
                                    {title}
                                </Text>
                            )}
                        </Text>
                        {isSelected && (
                            <CloseIcon
                                data-testid="close-icon"
                                {...styles.closeIcon}
                                onClick={() => {
                                    reset()
                                }}
                            />
                        )}
                    </HideOnMobile>
                    <HideOnDesktop {...styles.fullContainer}>
                        {isSelected ? (
                            <Flex {...styles.mobileSelectedContainer}>
                                <Stack {...styles.selectedCard}>
                                    <Text {...styles.subtitle}>{subtitle}</Text>
                                    <Text {...styles.title}>{title}</Text>
                                    <Text {...styles.text}>{text}</Text>
                                    <Button
                                        {...styles.buttonVariant}
                                        rightIcon={<ChevronRight {...styles.chevronRight} />}
                                        {...buttonProps}
                                    >
                                        {ctaLabel}
                                    </Button>
                                </Stack>
                                <CloseIcon
                                    {...styles.closeIconMobile}
                                    onClick={() => {
                                        reset()
                                    }}
                                />
                            </Flex>
                        ) : (
                            <Flex {...styles.mobileClosedCard}>
                                <Text {...styles.title}>{title}</Text>
                                <AddIcon {...styles.addIcon} />
                            </Flex>
                        )}
                    </HideOnDesktop>
                </>
            )}
        </Box>
    )
}

Card.propTypes = {
    linkIsAbsoulte: PropTypes.bool,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    isSelected: PropTypes.bool,
    onClick: PropTypes.func,
    noneSelected: PropTypes.bool,
    reset: PropTypes.func,
    index: PropTypes.number,
    cardsLength: PropTypes.number,
    text: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaUrl: PropTypes.string,
}

const ContentCards = ({contentData}) => {
    const styles = useMultiStyleConfig('ContentCards')
    const {locale} = useMultiSite()
    const categoriesData = useGetCategoriesData()
    const {card = []} = contentData
    const cards = card.length === 3 || card.length === 4 ? card : []
    const [selectedId, setSelectedId] = useState(null)
    const selectedCard = card.find((card) => card?._metadata.uid === selectedId)
    const damImage = useDamImage(
        selectedCard ? selectedCard?.card_images : contentData?.cover_image,
    )
    const {data: productsData} = useProducts({
        parameters: {
            ids: cards
                .filter((c) => c.button?.productsfcc?.data?.length > 0)
                .map((c) => c.button.productsfcc.data[0].id)
                .filter(Boolean)
                .join(','),
            allImages: false,
        },
    })
    return (
        <Stack
            background={`url(${damImage.url}) lightgray 50% / cover no-repeat`}
            {...styles.container}
        >
            {cards.map((card, index) => {
                const {url, label, isAbsolute} = getGlobalLink({
                    link: card?.button,
                    categoriesData,
                    productsData,
                    locale: locale?.alias || locale?.id?.toLowerCase(),
                })
                return (
                    <Card
                        title={card?.text_group?.title}
                        subtitle={card?.text_group?.label}
                        text={card?.text_group?.body_copy}
                        ctaLabel={label}
                        ctaUrl={url}
                        linkIsAbsoulte={isAbsolute}
                        isSelected={selectedId === card?._metadata.uid}
                        key={card._metadata.uid}
                        onClick={() => setSelectedId(card?._metadata.uid)}
                        reset={() => setSelectedId(null)}
                        noneSelected={selectedId === null}
                        cardsLength={cards.length}
                        index={index}
                    />
                )
            })}
        </Stack>
    )
}

ContentCards.propTypes = {
    contentData: PropTypes.shape({
        card: PropTypes.array,
        cover_image: PropTypes.array,
    }),
}

export default ContentCards
